// Account management utilities

export type AccountType = 'Free' | 'Professional' | 'Premium';

export interface AccountData {
  accountType: AccountType;
  responseCount: number;
  registrationDate: string; // ISO date string
}

export interface InvitedFriend {
  email: string;
  registered: boolean;
}

const ACCOUNT_STORAGE_KEY = 'routex_account_data';
const INVITED_FRIENDS_STORAGE_KEY = 'routex_invited_friends';
const DEFAULT_RESPONSE_COUNT = 10;
const MAX_INVITED_FRIENDS = 10;
const BONUS_RESPONSES_PER_REGISTERED_FRIEND = 2;

/**
 * Initialize account data for a new user
 */
export const initializeAccount = (userId: string): AccountData => {
  const accountData: AccountData = {
    accountType: 'Free',
    responseCount: DEFAULT_RESPONSE_COUNT,
    registrationDate: new Date().toISOString(),
  };

  localStorage.setItem(`${ACCOUNT_STORAGE_KEY}_${userId}`, JSON.stringify(accountData));
  return accountData;
};

/**
 * Get account data for a user
 */
export const getAccountData = (userId: string): AccountData | null => {
  const stored = localStorage.getItem(`${ACCOUNT_STORAGE_KEY}_${userId}`);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as AccountData;
  } catch {
    return null;
  }
};

/**
 * Get or initialize account data
 */
export const getOrInitializeAccount = (userId: string): AccountData => {
  const existing = getAccountData(userId);
  if (existing) {
    return existing;
  }
  return initializeAccount(userId);
};

/**
 * Decrement response count (uses effective count: base + bonus from registered invites)
 */
export const decrementResponseCount = (userId: string): number | null => {
  const accountData = getAccountData(userId);
  if (!accountData) return null;
  if (getEffectiveResponseCount(userId) <= 0) return accountData.responseCount;

  accountData.responseCount -= 1;
  localStorage.setItem(`${ACCOUNT_STORAGE_KEY}_${userId}`, JSON.stringify(accountData));
  return accountData.responseCount;
};

/**
 * Update account type.
 * When switching to Free, response remaining is reset to the value a new account gets (10).
 */
export const updateAccountType = (userId: string, accountType: AccountType): void => {
  const accountData = getAccountData(userId);
  if (!accountData) {
    initializeAccount(userId);
    return;
  }

  accountData.accountType = accountType;
  if (accountType === 'Free') {
    accountData.responseCount = DEFAULT_RESPONSE_COUNT;
  }
  localStorage.setItem(`${ACCOUNT_STORAGE_KEY}_${userId}`, JSON.stringify(accountData));
};

// --- Invited friends (up to 10, +2 agent calls per registered friend) ---

export const getInvitedFriends = (userId: string): InvitedFriend[] => {
  const stored = localStorage.getItem(`${INVITED_FRIENDS_STORAGE_KEY}_${userId}`);
  if (!stored) return [];
  try {
    return JSON.parse(stored) as InvitedFriend[];
  } catch {
    return [];
  }
};

const saveInvitedFriends = (userId: string, friends: InvitedFriend[]): void => {
  localStorage.setItem(`${INVITED_FRIENDS_STORAGE_KEY}_${userId}`, JSON.stringify(friends));
};

export const addInvitedFriend = (userId: string, email: string): { ok: boolean; error?: string } => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return { ok: false, error: 'Enter an email' };
  const friends = getInvitedFriends(userId);
  if (friends.length >= MAX_INVITED_FRIENDS) return { ok: false, error: 'Maximum 10 friends' };
  if (friends.some(f => f.email.toLowerCase() === normalized)) return { ok: false, error: 'Already invited' };
  friends.push({ email: normalized, registered: false });
  saveInvitedFriends(userId, friends);
  return { ok: true };
};

export const removeInvitedFriend = (userId: string, email: string): void => {
  const friends = getInvitedFriends(userId).filter(
    f => f.email.toLowerCase() !== email.trim().toLowerCase()
  );
  saveInvitedFriends(userId, friends);
};

export const setFriendRegistered = (userId: string, email: string): void => {
  const normalized = email.trim().toLowerCase();
  const friends = getInvitedFriends(userId).map(f =>
    f.email.toLowerCase() === normalized ? { ...f, registered: true } : f
  );
  saveInvitedFriends(userId, friends);
};

export const getRegisteredInvitesCount = (userId: string): number => {
  return getInvitedFriends(userId).filter(f => f.registered).length;
};

/**
 * Get effective response count (base + 2 per registered invited friend, for Free tier)
 */
export const getEffectiveResponseCount = (userId: string): number => {
  const accountData = getAccountData(userId);
  if (!accountData) return DEFAULT_RESPONSE_COUNT;
  if (accountData.accountType === 'Professional' || accountData.accountType === 'Premium') {
    return accountData.responseCount; // Unlimited displayed as-is
  }
  const registeredCount = getRegisteredInvitesCount(userId);
  return accountData.responseCount + registeredCount * BONUS_RESPONSES_PER_REGISTERED_FRIEND;
};

/**
 * Check if account can use the app (has responses remaining or is Professional/Premium)
 */
export const canUseApp = (userId: string): boolean => {
  const accountData = getAccountData(userId);
  if (!accountData) return true; // Allow if no account data (will be initialized)

  // Professional and Premium accounts can always use the app
  if (accountData.accountType === 'Professional' || accountData.accountType === 'Premium') {
    return true;
  }

  // Free accounts: check effective response count (base + bonus from registered invites)
  return getEffectiveResponseCount(userId) > 0;
};
